import { createContext, useEffect, useLayoutEffect, useState } from 'react'
import { Routes, Route, useLocation, Location } from 'react-router-dom'
import { motion, useMotionValue, animate } from 'framer-motion'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import LandingPage from '../landing'
import PricingPage from '../pricing'
import FeaturesPage from '../features'
import IntegrationsPage from '../integrations'
// import CortexPage from '../cortex'
import AgentsPage from '../agents'
import AboutPage from '../about'
import CareersPage from '../careers'
import PressPage from '../press'
import ContactPage from '../contact'
import PrivacyPage from '../privacy'
import TermsPage from '../terms'
import GDPRPage from '../gdpr'
import StatusPage from '../status'
import UpdatesPage from '../updates'
import NanzaPrivacyPage from '../nanza/privacy'

export const RouteTransitionContext = createContext<boolean>(false)

const SLIDE_EASE = [0.65, 0, 0.35, 1] as const
const SLIDE_DURATION = 0.85

type Slot = 'A' | 'B'

const SLIDING_CLASS =
  'fixed inset-x-0 top-0 z-40 h-screen overflow-hidden bg-white shadow-[0_-20px_60px_rgba(0,0,0,0.18)]'

function RouteTree({ location }: { location: Location }) {
  return (
    <Routes location={location}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      {/* <Route path="/cortex" element={<CortexPage />} /> */}
      <Route path="/agents" element={<AgentsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/gdpr" element={<GDPRPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/updates" element={<UpdatesPage />} />
      <Route path="/nanza/privacy" element={<NanzaPrivacyPage />} />
    </Routes>
  )
}

export default function AnimatedRoutes() {
  const location = useLocation()
  const [slotA, setSlotA] = useState<Location | null>(location)
  const [slotB, setSlotB] = useState<Location | null>(null)
  const [active, setActive] = useState<Slot>('A')
  const [slidingSlot, setSlidingSlot] = useState<Slot | null>(null)
  const y = useMotionValue('0%')

  // Kick off the slide: put the new location in the idle slot, park it off-screen,
  // mark it as sliding. The active slot stays mounted in normal flow the whole time.
  useLayoutEffect(() => {
    if (slidingSlot !== null) return
    const activeLoc = active === 'A' ? slotA : slotB
    if (!activeLoc || activeLoc.pathname === location.pathname) return

    const target: Slot = active === 'A' ? 'B' : 'A'
    if (target === 'A') setSlotA(location)
    else setSlotB(location)
    y.set('100%')
    setSlidingSlot(target)
  }, [location, slidingSlot, active, slotA, slotB, y])

  // Run the slide. On completion: promote the incoming slot to active and
  // unmount the outgoing one. The incoming page's mount is preserved across
  // this flip — className just drops from fixed to normal flow.
  useEffect(() => {
    if (!slidingSlot) return
    let cancelled = false
    const controls = animate(y, '0%', {
      duration: SLIDE_DURATION,
      ease: SLIDE_EASE,
    })
    controls.then(() => {
      if (cancelled) return
      // Reset scroll now, while the incoming page is still fixed over the
      // viewport. The outgoing page (normal flow, possibly scrolled) moves
      // invisibly beneath it. `instant` bypasses `scroll-behavior: smooth`
      // on <html>, which would otherwise animate the reset into view.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
      const completed = slidingSlot
      const old: Slot = completed === 'A' ? 'B' : 'A'
      setActive(completed)
      if (old === 'A') setSlotA(null)
      else setSlotB(null)
      setSlidingSlot(null)
      // After the incoming page drops from fixed overlay to normal flow,
      // recompute all ScrollTriggers with correct measurements. Delay enough
      // for React to finish committing and for the browser to settle layout.
      setTimeout(() => {
        ScrollTrigger.refresh()
        // Second pass once fonts/images finish painting.
        setTimeout(() => ScrollTrigger.refresh(), 200)
      }, 50)
      if (location.hash) {
        requestAnimationFrame(() => {
          const el = document.querySelector(location.hash)
          if (el) el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' })
        })
      }
    })
    return () => {
      cancelled = true
      controls.stop()
    }
  }, [slidingSlot, y, location])

  return (
    <RouteTransitionContext.Provider value={slidingSlot !== null}>
      {slotA && (slidingSlot === 'A' ? (
        <motion.div style={{ y }} className={SLIDING_CLASS}>
          <RouteTree location={slotA} />
        </motion.div>
      ) : (
        <div>
          <RouteTree location={slotA} />
        </div>
      ))}
      {slotB && (slidingSlot === 'B' ? (
        <motion.div style={{ y }} className={SLIDING_CLASS}>
          <RouteTree location={slotB} />
        </motion.div>
      ) : (
        <div>
          <RouteTree location={slotB} />
        </div>
      ))}
    </RouteTransitionContext.Provider>
  )
}
